import { useMemo, useState } from "react";
import { useImmer } from "use-immer";

import { IgFilterAutocomplete } from "@intelligems/ig-theme";
import {
  ChannelType,
  ConversionStageReachedType,
  CountryEnum,
  CustomEventsFilterType,
  DeviceType,
  ExperienceCategory,
  GenericExperienceFe,
  LandingPageFilterType,
  StatSigViewType,
  UrlMatch,
  UrlParamPayloadType,
  VisitorType,
} from "@intelligems/ig-types";
import { getTinybirdActiveAnalyticsFilters } from "@src/api/analytics/utils";
import { useConfiguration } from "@src/api/configurations/use-configuration";
import { usePatchExperience } from "@src/api/experiences/patch-experience";
import type { AnalyticFiltersConfigType } from "@src/api/types/dashboards";
import { DashboardPageName } from "@src/api/types/dashboards";
import { AnalyticsFiltersDrawer } from "@src/features/analytics/analyticsFilters/AnalyticsFiltersDrawer";
import { AnalyticsViewTypeFilter } from "@src/features/analytics/analyticsFilters/AnalyticsViewTypeFilter";
import { CustomEventsFilter } from "@src/features/analytics/analyticsFilters/CustomEventsFilter";
import { DeviceTypeFilter } from "@src/features/analytics/analyticsFilters/DeviceTypeFilter";
import { UserBehaviorFilter } from "@src/features/analytics/analyticsFilters/UserBehaviorFilter";
import {
  getAllChannelTypes,
  getChannelTypeListCount,
  useDialogFilterValidation,
} from "@src/features/analytics/analyticsFilters/utils";
import { VisitorTypeFilter } from "@src/features/analytics/analyticsFilters/VisitorTypeFilter";
import { useDispatch, useSelector } from "@src/store";
import { getAnalyticsProducts } from "@src/store/slices/analyticsProducts";
import { setDashboardFilter } from "@src/store/slices/dashboardFilters";
import { closeDrawer } from "@src/store/slices/drawers";
import { getCustomMetrics } from "@src/store/slices/experienceMetrics/experienceMetrics";
import { selectedExperience } from "@src/store/slices/experiences";
import { setSitewideDashboardFilter } from "@src/store/slices/sitewideDashboardFilters";
import { IExperienceDashboardFilters } from "@src/store/types/dashboardFilters";
import { mpClickAnalyticsFilterSaveButton } from "@src/utils/mixpanel";

import { ChannelChecklist } from "../../../components/AudienceFilters/Channel";

import { LandingPageFilter } from "./LandingPageFilter";
import { UrlParamFilter } from "./UrlParamFilter";
import { ZScoreFilter } from "./ZScoreFilter";

interface AnalyticsFilterDrawerWrapperProps {
  pageName?: DashboardPageName;
  currentExperience?: GenericExperienceFe;
  openFilters: boolean;
  renderLocation: "sitewide" | "experience";
  allCountryCodesData?: string[];
}

export const AnalyticsFilterDrawerWrapper = ({
  currentExperience,
  pageName,
  openFilters,
  renderLocation,
  allCountryCodesData,
}: AnalyticsFilterDrawerWrapperProps) => {
  const dispatch = useDispatch();
  const experience = useSelector(selectedExperience);
  const dashboardFilters = useSelector((state) => state.dashboardFilters);
  const mutateExperience = usePatchExperience(currentExperience?.id);
  const experienceCustomMetrics = useSelector(getCustomMetrics);
  const analyticsProducts = useSelector(getAnalyticsProducts);
  const maxZScoreConfig = useConfiguration("max_z_score");
  const isHighVolumeAnalytics = useConfiguration("is_high_volume_analytics");

  const [dialogFilters, setDialogFilters] =
    useImmer<IExperienceDashboardFilters>({
      ...dashboardFilters,
      statSigView: dashboardFilters.statSigView ?? "best",
      testOrdersOnly:
        renderLocation === "sitewide"
          ? false
          : dashboardFilters.testOrdersOnly !== undefined
            ? dashboardFilters.testOrdersOnly
            : currentExperience?.experienceAnalysis.analyticsViewType ===
                "none" ||
              currentExperience?.experienceAnalysis.analyticsViewType ===
                "test_orders_only",
      discountIncludeType: dashboardFilters.discountIncludeType || 1,
      isZScoreEnabled: dashboardFilters.isZScoreEnabled ?? true,
      maxZScore: dashboardFilters.maxZScore ?? maxZScoreConfig!.value,
      customEvents: dashboardFilters.customEvents || [],
      countryCodes: dashboardFilters.countryCodes || [],
      userBehavior: dashboardFilters.userBehavior ?? {
        viewedCheckout: null,
        addedToCart: null,
        viewedCollectionPage: null,
        viewedProductPage: null,
      },
      startTime: null,
      endTime: null,
      customEventsFilterType: dashboardFilters.customEventsFilterType || "all",
      deviceType: dashboardFilters.deviceType || "any",
      visitorType: dashboardFilters.visitorType || "any",
      channels:
        dashboardFilters.channels.length === 0
          ? getAllChannelTypes()
          : dashboardFilters.channels,
      reachedCheckoutStage: dashboardFilters.reachedCheckoutStage || "any",
      urlParam: {
        queryParam:
          dashboardFilters.urlParam?.queryParam !== null
            ? dashboardFilters.urlParam?.queryParam
            : null,
        queryParamValue:
          dashboardFilters.urlParam?.queryParamValue !== null
            ? dashboardFilters.urlParam?.queryParamValue
            : null,
        queryParamFilterType:
          dashboardFilters.urlParam?.queryParamFilterType !== null
            ? dashboardFilters.urlParam?.queryParamFilterType
            : null,
      },
    });

  const [selectedCustomEventIdentifiers, setSelectedCustomEventIdentifiers] =
    useImmer<string[]>(dashboardFilters.customEvents ?? []);
  const [isTestProductsDefaultChange, setIsTestProductsDefaultChange] =
    useState<boolean>(false);
  const validationErrors = useDialogFilterValidation(dialogFilters);
  const handleCustomEventSelect = (identifier: string) => {
    setSelectedCustomEventIdentifiers((draft) => {
      const index = draft.indexOf(identifier);
      if (index === -1) {
        draft.push(identifier); // Add if not already selected
      } else {
        draft.splice(index, 1); // Remove if already selected
      }
    });

    const updatedSelection = [...selectedCustomEventIdentifiers];
    const index = updatedSelection.indexOf(identifier);
    if (index === -1) {
      updatedSelection.push(identifier);
    } else {
      updatedSelection.splice(index, 1);
    }

    setDialogFilters((draft) => {
      draft.customEvents = updatedSelection;
      return draft;
    });
  };

  const onStringFilterChange = (
    newValue:
      | DeviceType
      | VisitorType
      | ChannelType
      | StatSigViewType
      | ConversionStageReachedType
      | CustomEventsFilterType,
    filterKey:
      | "statSigView"
      | "deviceType"
      | "channelType"
      | "visitorType"
      | "reachedCheckoutStage"
      | "customEventsFilterType"
  ) => {
    setDialogFilters((draft) => {
      draft[filterKey] = newValue;
      return draft;
    });
  };

  const onBooleanFilterChange = (
    checked: boolean,
    filterKey: "testOrdersOnly" | "isZScoreEnabled"
  ) => {
    setDialogFilters((draft) => {
      draft[filterKey] = checked;
      return draft;
    });
  };

  const onStringArrFilterChange = (
    newValues: string[],
    filterKey: "customEvents" | "countryCodes"
  ) => {
    setDialogFilters((draft) => {
      draft[filterKey] = newValues;
      return draft;
    });
  };

  const onChannelClick = (channelType: ChannelType | null) => {
    const updatedChannels = dialogFilters.channels.filter(
      (channel) => !!channel
    );
    if (!channelType) {
      // handles all channels button
      setDialogFilters((draft) => {
        draft.channels =
          draft.channels.length === getChannelTypeListCount()
            ? []
            : getAllChannelTypes();
        return draft;
      });
    } else if (updatedChannels.includes(channelType)) {
      // handles removing channel when un selected
      setDialogFilters((draft) => {
        draft.channels = draft.channels.filter(
          (channel) => channel !== channelType
        );
        return draft;
      });
    } else {
      // handles adding unselected channels when selected
      setDialogFilters((draft) => {
        draft.channels = [...(updatedChannels || []), channelType];
      });
    }
  };

  const onUrlParamChange = (
    value: string | null,
    key: keyof UrlParamPayloadType
  ) => {
    setDialogFilters((draft) => {
      if (!draft.urlParam) {
        draft.urlParam = {
          queryParam: null,
          queryParamFilterType: null,
          queryParamValue: null,
        };
        // @ts-ignore
        draft.urlParam[key] = value;
      } else {
        draft.urlParam[key] = value as UrlMatch;
      }
      return draft;
    });
  };

  const onLandingPageChange = (
    value: string | null,
    filter: LandingPageFilterType | null
  ) => {
    setDialogFilters((draft) => {
      draft.landingPage = {
        ...draft.landingPage,
        landingPageUrlPath: value,
        landingPageUrlPathFilterType: filter,
      };
    });
  };

  const filterButtons: AnalyticFiltersConfigType[] = useMemo(() => {
    return [
      {
        label: "Device Type",
        settings: [
          {
            key: "deviceType",
            description: "Filter by device type",
            component: (
              <DeviceTypeFilter
                onStringFilterChange={onStringFilterChange}
                deviceType={dialogFilters.deviceType}
              />
            ),
          },
        ],
        isSwitch: false,
        showOnPage: "all",
      },
      {
        label: "Visitor Type",
        settings: [
          {
            key: "visitorType",
            description: "Filter by visitor type",
            component: (
              <VisitorTypeFilter
                onStringFilterChange={onStringFilterChange}
                visitorType={dialogFilters.visitorType}
              />
            ),
          },
        ],
        isSwitch: false,
        showOnPage: "all",
      },
      {
        label: "Source Channels & Sites",
        settings: [
          {
            key: "channelType",
            description:
              "Filter by one more more channel and traffic source type",
            component: (
              <ChannelChecklist
                onClick={onChannelClick}
                channels={dialogFilters.channels}
              />
            ),
          },
        ],
        isSwitch: false,
        showOnPage: "all",
      },
      {
        label: "URL Parameter",
        settings: [
          {
            key: "urlParam",
            description:
              "Filter to visitors from specific ad experiences or media",
            component: (
              <UrlParamFilter
                onUrlParamChange={onUrlParamChange}
                urlParam={dialogFilters.urlParam}
                onReset={() => {
                  setDialogFilters((draft) => {
                    draft.urlParam = {
                      queryParam: null,
                      queryParamValue: null,
                      queryParamFilterType: null,
                    };
                    return draft;
                  });
                }}
              />
            ),
          },
        ],
        isSwitch: false,
        showOnPage: "all",
        config:
          (isHighVolumeAnalytics?.value === false ||
            isHighVolumeAnalytics?.value === undefined) &&
          renderLocation !== "sitewide",
      },
      {
        label: "User Behavior",
        settings: [
          {
            key: "userBehavior",
            component: (
              <UserBehaviorFilter
                onStringFilterChange={onStringFilterChange}
                conversionStageReached={
                  dialogFilters.reachedCheckoutStage ?? "any"
                }
                setDialogFilters={setDialogFilters}
                userBehavior={dialogFilters.userBehavior}
              />
            ),
          },
        ],
        isSwitch: false,
        showOnPage: "all",
        config: renderLocation !== "sitewide",
      },
      {
        label: "Custom Events",
        settings: [
          {
            key: "customEvents",
            component: (
              <CustomEventsFilter
                customEventsFilterType={
                  dialogFilters.customEventsFilterType ?? "all"
                }
                onChange={onStringFilterChange}
                selectedCustomEventIds={selectedCustomEventIdentifiers}
                onSelect={handleCustomEventSelect}
              />
            ),
          },
        ],
        isSwitch: false,
        config:
          currentExperience !== undefined &&
          currentExperience.category === "experiment" &&
          experienceCustomMetrics.length > 0,
        showOnPage: "all",
      },
      {
        label: "Filter Orders by Product",
        settings: [
          {
            key: "testProdSwitch",
            description:
              "Limit analytics to orders containing specific products",
            component: (
              <AnalyticsViewTypeFilter
                currentExperience={currentExperience}
                testOrdersOnly={dialogFilters.testOrdersOnly ?? false}
                onBooleanFilterChange={onBooleanFilterChange}
                setIsTestProductsDefaultChange={setIsTestProductsDefaultChange}
                isTestProductsDefaultChange={isTestProductsDefaultChange}
              />
            ),
          },
        ],
        config:
          (currentExperience !== undefined &&
            currentExperience.testTypes.hasTestPricing) ||
          analyticsProducts?.length > 0 ||
          (currentExperience !== undefined &&
            "offerProducts" in currentExperience &&
            currentExperience.offerProducts?.length > 0),
        showOnPage: "all",
        hasDefault: true,
        tooltip: "Target products can be updated in your test setup flow.",
      },
      {
        label: "Z-Score",
        settings: [
          {
            key: "isZScoreEnabled",
            description: "Max Z-Score",
            component: (
              <ZScoreFilter
                isZScoreEnabled={dialogFilters.isZScoreEnabled}
                setDialogFilters={setDialogFilters}
                maxZScore={dialogFilters.maxZScore}
                onBooleanFilterChange={onBooleanFilterChange}
              />
            ),
          },
        ],
        isSwitch: true,
        showOnPage: "all",
      },
      {
        label: "Countries",
        settings: [
          {
            key: "countryCodes",
            description:
              "Filter to visitors who were in one of these countries",
            component: (
              <IgFilterAutocomplete
                selectedValues={dialogFilters.countryCodes}
                onChange={onStringArrFilterChange}
                filterKey={"countryCodes"}
                allDataOptions={allCountryCodesData!}
                placeholder={"Countries"}
                disableCloseOnSelect={true}
                getOptionDisabled={(option) => option === null}
                getOptionLabel={(option) =>
                  `${CountryEnum[option] ?? "Unknown"} ${option ? `(${option})` : ""}`
                }
              />
            ),
          },
        ],
        isSwitch: false,
        showOnPage: "all",
        config:
          currentExperience &&
          allCountryCodesData &&
          allCountryCodesData?.length > 0,
      },
      {
        label: "Landing Page",
        isSwitch: false,
        showOnPage: "all",
        settings: [
          {
            key: "landingPage",
            description: "Filter to visitors by their landing page.",
            component: (
              <LandingPageFilter
                value={dialogFilters.landingPage?.landingPageUrlPath || null}
                filter={
                  dialogFilters.landingPage?.landingPageUrlPathFilterType ||
                  null
                }
                onChange={onLandingPageChange}
              />
            ),
          },
        ],
      },
    ];
  }, [
    isHighVolumeAnalytics?.value,
    dialogFilters,
    currentExperience,
    allCountryCodesData,
    analyticsProducts?.length,
    selectedCustomEventIdentifiers,
  ]);

  const onFiltersClearAll = () => {
    const initialTestOrdersOnly =
      renderLocation === "sitewide"
        ? false
        : currentExperience && currentExperience.category === "personalization"
          ? true
          : currentExperience?.experienceAnalysis.analyticsViewType ===
              "none" ||
            currentExperience?.experienceAnalysis.analyticsViewType ===
              "test_orders_only";

    dispatch(
      setDashboardFilter({
        ...dashboardFilters,
        testOrdersOnly: initialTestOrdersOnly,
        isZScoreEnabled: true,
        deviceType: null,
        discountIncludeType: 1,
        visitorType: null,
        channels: [],
        maxZScore: maxZScoreConfig!.value,
        customEvents: [],
        countryCodes: [],
        userBehavior: {
          viewedCheckout: null,
          addedToCart: null,
          viewedCollectionPage: null,
          viewedProductPage: null,
        },
        reachedCheckoutStage: "any",
        urlParam: {
          queryParam: null,
          queryParamFilterType: null,
          queryParamValue: null,
        },
        landingPage: {
          landingPageUrlPath: null,
          landingPageUrlPathFilterType: null,
        },
      })
    );

    setDialogFilters((draft) => {
      draft.testOrdersOnly = initialTestOrdersOnly;
      draft.discountIncludeType = 1;
      draft.isZScoreEnabled = true;
      draft.maxZScore = maxZScoreConfig!.value;
      draft.deviceType = null;
      draft.channels = [];
      draft.visitorType = null;
      draft.customEvents = [];
      draft.countryCodes = [];
      draft.userBehavior = {
        viewedCheckout: null,
        addedToCart: null,
        viewedCollectionPage: null,
        viewedProductPage: null,
      };
      draft.reachedCheckoutStage = "any";
      draft.urlParam = {
        queryParam: null,
        queryParamFilterType: null,
        queryParamValue: null,
      };
      draft.landingPage = {
        landingPageUrlPath: null,
        landingPageUrlPathFilterType: null,
      };
      return draft;
    });

    dispatch(closeDrawer("analyticsFilters"));
  };

  const onFiltersSave = () => {
    if (isTestProductsDefaultChange && currentExperience) {
      setIsTestProductsDefaultChange(false);
      mutateExperience.mutate({
        id: currentExperience.id!,
        analyticsViewType: dialogFilters.testOrdersOnly
          ? "test_orders_only"
          : "sitewide_orders",
      });
    }

    const { startTime, endTime, ...rest } = [dialogFilters].map((filters) => {
      return {
        ...filters,
        channels:
          filters.channels.length === getChannelTypeListCount()
            ? []
            : filters.channels,
      };
    })[0];
    const getTbActiveFilters = getTinybirdActiveAnalyticsFilters(
      {
        ...dashboardFilters,
        ...rest,
      },
      maxZScoreConfig!.value
    );
    if (Object.keys(getTbActiveFilters).length) {
      mpClickAnalyticsFilterSaveButton(
        experience.id!,
        getTbActiveFilters,
        pageName || "overview"
      );
    }
    dispatch(setDashboardFilter({ ...dashboardFilters, ...rest }));
    dispatch(closeDrawer("analyticsFilters"));
  };

  return (
    <AnalyticsFiltersDrawer
      onFilterSave={onFiltersSave}
      openFilters={openFilters}
      onFiltersClearAll={onFiltersClearAll}
      filterButtons={filterButtons}
      isSaveLoading={mutateExperience.isPaused}
      pageName={pageName}
      isSaveDisabled={validationErrors[0] ?? { isDisabled: false, reason: "" }}
    />
  );
};
